import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Textarea } from '@/components/Common/textarea';
import { Button } from '@/components/Common/button';
import { Send, Loader2, Paperclip, FileText, FileDown, X, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { conversationsService, ConversationUpload } from '@/services/conversations';
import type { ShortcutButton } from '@/services/chatbots';
import notify from '@/lib/notify';

// Server accepts 17 formats; these are the sensible ones to offer in the picker
const ACCEPTED_UPLOAD_FORMATS = '.pdf,.docx,.doc,.pptx,.xlsx,.csv,.txt,.md';
const MAX_UPLOADS_PER_CONVERSATION = 10;
const MAX_UPLOAD_SIZE_BYTES = 50 * 1024 * 1024;

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

interface MessageInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Rounded “pill” composer used on the full-page chat */
  variant?: 'default' | 'chat';
  /** Active conversation id — enables file attachments (list/upload/remove) */
  conversationId?: string;
  /**
   * Creates the conversation on demand and returns its id — used when the user
   * attaches a file before sending the first message.
   */
  onEnsureConversation?: () => Promise<string>;
  /** Agent-defined shortcut chips rendered above the composer */
  shortcutButtons?: ShortcutButton[];
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  disabled,
  autoFocus = false,
  variant = 'default',
  conversationId,
  onEnsureConversation,
  shortcutButtons,
}) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [uploads, setUploads] = useState<ConversationUpload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canAttach = Boolean(conversationId || onEnsureConversation);

  // Auto-focus when disabled changes to false
  useEffect(() => {
    if (!disabled && autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled, autoFocus]);

  // Load attachments when the active conversation changes
  useEffect(() => {
    if (!conversationId) {
      setUploads([]);
      return;
    }

    let cancelled = false;
    conversationsService
      .listConversationUploads(conversationId)
      .then((data) => {
        if (cancelled) return;
        // Merge with any upload completed while this fetch was in flight
        // (happens when attaching creates the conversation)
        setUploads((prev) => {
          const byId = new Map(data.map((upload) => [upload.id, upload]));
          for (const upload of prev) {
            if (upload.conversation_id === conversationId && !byId.has(upload.id)) {
              byId.set(upload.id, upload);
            }
          }
          return Array.from(byId.values());
        });
      })
      .catch((error) => {
        console.error('Error loading conversation uploads:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSend(message.trim());
      setMessage('');
      // Re-focus after sending
      if (autoFocus && inputRef.current) {
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAttachClick = () => {
    if (uploads.length >= MAX_UPLOADS_PER_CONVERSATION) {
      notify.error(t('chatUploads.limitReached'));
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Allow re-selecting the same file later
    e.target.value = '';
    if (!file) return;

    if (file.size === 0) {
      notify.error(t('chatUploads.emptyFile'));
      return;
    }
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      notify.error(t('chatUploads.fileTooLarge'));
      return;
    }

    setIsUploading(true);
    try {
      let targetConversationId = conversationId;
      if (!targetConversationId) {
        if (!onEnsureConversation) return;
        targetConversationId = await onEnsureConversation();
      }

      const upload = await conversationsService.uploadConversationFile(targetConversationId, file);
      setUploads((prev) => [...prev, upload]);
      if (upload.status === 'failed') {
        notify.warning(upload.error_message || t('chatUploads.extractionFailed'));
      }
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      const description =
        status === 409
          ? t('chatUploads.limitReached')
          : status === 413
            ? t('chatUploads.fileTooLarge')
            : status === 400
              ? t('chatUploads.emptyFile')
              : t('chatUploads.uploadFailed', { name: file.name });
      notify.error(description);
    } finally {
      setIsUploading(false);
    }
  };

  const handleShortcutClick = async (button: ShortcutButton) => {
    if (disabled) return;

    // Plain prompt shortcut — behaves exactly like typing the prompt and sending it.
    if (!button.template_id) {
      onSend(button.prompt);
      return;
    }

    // Template shortcut — generate a document from the conversation.
    if (isGenerating) return;
    setIsGenerating(true);
    try {
      let targetConversationId = conversationId;
      if (!targetConversationId) {
        if (!onEnsureConversation) return;
        targetConversationId = await onEnsureConversation();
      }

      const { blob, filename } = await conversationsService.generateConversationFile(
        targetConversationId,
        { template_id: button.template_id, instructions: button.prompt }
      );
      downloadBlob(blob, filename);
    } catch (error) {
      console.error('Error generating file from shortcut:', error);
      notify.error(t('chatGenerate.failed'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRemoveUpload = async (upload: ConversationUpload) => {
    try {
      await conversationsService.deleteConversationUpload(upload.conversation_id, upload.id);
      setUploads((prev) => prev.filter((item) => item.id !== upload.id));
    } catch (error) {
      console.error('Error removing conversation upload:', error);
      notify.error(t('chatUploads.removeFailed'));
    }
  };

  const placeholder = t('aiChatbot.placeholder');

  const hiddenFileInput = canAttach ? (
    <input
      ref={fileInputRef}
      type="file"
      accept={ACCEPTED_UPLOAD_FORMATS}
      className="hidden"
      onChange={handleFileSelected}
      aria-hidden="true"
      tabIndex={-1}
    />
  ) : null;

  const attachmentChips =
    canAttach && (uploads.length > 0 || isUploading) ? (
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        {uploads.map((upload) => (
          <span
            key={upload.id}
            title={
              upload.status === 'failed'
                ? upload.error_message || t('chatUploads.extractionFailed')
                : upload.filename
            }
            className={cn(
              'inline-flex max-w-[240px] items-center gap-1 rounded-full border px-2.5 py-1 text-xs',
              upload.status === 'failed'
                ? 'border-destructive/50 bg-destructive/10 text-destructive'
                : 'border-border bg-muted/40 text-foreground'
            )}
          >
            <FileText className="h-3 w-3 shrink-0" aria-hidden />
            <span className="truncate">{upload.filename}</span>
            <button
              type="button"
              onClick={() => handleRemoveUpload(upload)}
              className="shrink-0 rounded-full opacity-70 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('chatUploads.removeFile', { name: upload.filename })}
              title={t('chatUploads.removeFile', { name: upload.filename })}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        ))}
        {isUploading && (
          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            {t('chatUploads.uploading')}
          </span>
        )}
      </div>
    ) : null;

  // Agent-defined shortcut chips (GL-4.3/4.5), rendered under the attachments row.
  const shortcutChips =
    shortcutButtons && shortcutButtons.length > 0 ? (
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        {shortcutButtons.map((button, index) => {
          const isTemplateButton = Boolean(button.template_id);
          const isDisabled =
            disabled ||
            (isTemplateButton && (isGenerating || (!conversationId && !onEnsureConversation)));
          return (
            <button
              key={`${button.label}-${index}`}
              type="button"
              onClick={() => void handleShortcutClick(button)}
              disabled={isDisabled}
              title={button.prompt}
              aria-label={button.label}
              className={cn(
                'inline-flex max-w-[280px] items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs text-foreground',
                'transition-colors hover:bg-muted/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            >
              {isTemplateButton ? (
                isGenerating ? (
                  <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
                ) : (
                  <FileDown className="h-3 w-3 shrink-0" aria-hidden />
                )
              ) : (
                <Sparkles className="h-3 w-3 shrink-0" aria-hidden />
              )}
              <span className="truncate">{button.label}</span>
            </button>
          );
        })}
      </div>
    ) : null;

  const attachButton = canAttach ? (
    <Button
      onClick={handleAttachClick}
      disabled={disabled || isUploading}
      size="icon"
      variant="ghost"
      className={cn(
        'shrink-0 text-muted-foreground hover:text-foreground',
        variant === 'chat' ? 'mb-0.5 h-9 w-9 rounded-full' : 'self-end'
      )}
      aria-label={t('chatUploads.attachFile')}
      title={t('chatUploads.attachFile')}
    >
      {isUploading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Paperclip className="h-4 w-4" />
      )}
    </Button>
  ) : null;

  if (variant === 'chat') {
    return (
      <div>
        {attachmentChips}
        {shortcutChips}
        {hiddenFileInput}
        <div
          className={cn(
            'flex items-end gap-2 rounded-2xl border border-border/80 bg-muted/20 px-3 py-2 shadow-sm',
            'transition-shadow focus-within:border-border focus-within:shadow-md focus-within:ring-1 focus-within:ring-ring/20'
          )}
        >
          <Textarea
            ref={inputRef}
            markdown={false}
            value={message}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className="max-h-[200px] min-h-[44px] flex-1 resize-none border-0 bg-transparent px-0 py-2 text-[15px] shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            aria-label={t('aiChatbot.placeholder')}
            aria-describedby="message-hint"
          />
          <span id="message-hint" className="sr-only">
            {t('aiChatbot.enterToSend')}
          </span>
          {attachButton}
          <Button
            onClick={handleSend}
            disabled={disabled || !message.trim()}
            size="icon"
            className="mb-0.5 h-9 w-9 shrink-0 rounded-full"
            aria-label={disabled ? t('aiChatbot.thinking') : t('aiChatbot.send')}
          >
            {disabled ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {attachmentChips}
      {shortcutChips}
      {hiddenFileInput}
      <div className="flex gap-2">
        <Textarea
          ref={inputRef}
          markdown={false}
          value={message}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          rows={3}
          className="resize-none"
          aria-label={t('aiChatbot.placeholder')}
          aria-describedby="message-hint"
        />
        <span id="message-hint" className="sr-only">
          {t('aiChatbot.enterToSend')}
        </span>
        {attachButton}
        <Button
          onClick={handleSend}
          disabled={disabled || !message.trim()}
          size="icon"
          className="h-auto"
          aria-label={disabled ? t('aiChatbot.thinking') : t('aiChatbot.send')}
        >
          {disabled ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
};
